// Safe Supabase client — proxies the real client with a hard impersonation guard.
//
// While `isImpersonating()` is true, ANY mutating call against the proxy
// (`.insert`, `.update`, `.delete`, `.upsert`, write `.rpc()`, write `.functions.invoke()`)
// is intercepted and silently no-ops, returning a synthetic success without
// touching the network.
//
// Reads (`.select`, allowlisted read-only `.rpc` / edge functions), `.auth`,
// `.storage`, and `.realtime` pass through unchanged.
//
// The impersonation flag is a module-level boolean set by ImpersonationContext
// when impersonation starts / stops. Synchronous so any code path — including
// promise resolution inside a chained builder — can check it without await.
//
// Every part of the app that needs DB access should import `supabase` from
// THIS file, not from `@/integrations/supabase/client` directly. The only
// allowed direct import of the real client is below.

import { supabase as realSupabase } from '@/integrations/supabase/client';

let impersonating = false;

/** Flip to true the moment impersonation starts, false the moment it stops. */
export function setImpersonating(v: boolean): void {
  impersonating = v;
}

export function isImpersonating(): boolean {
  return impersonating;
}

// Allowlist of read-only RPCs that are safe during impersonation. Default deny:
// anything not here is no-op'd. Add entries explicitly as needs are identified.
export const READ_ONLY_RPCS: ReadonlySet<string> = new Set<string>([
  // Team Management reads — pure SELECTs behind SECURITY DEFINER, no writes.
  'get_hierarchy_tree',
  'get_client_scenarios',
  // Sidebar split — pure boolean read. Without this, impersonating an
  // inner-circle user hid their Account lane (2026-09-04 incident).
  'is_platform_staff_org',
  // Subscription walk-up for the lock screen — pure boolean read.
  'org_chain_has_valid_subscription',
  // Billing page seat count for the branch — pure read, counts only.
  'get_seat_usage',
  // Pre-login invite peephole — SECURITY DEFINER read of three safe fields.
  'get_invite_preview',
  // Alts / FIA licence for the effective user — pure read; under View-as the
  // wizard must withdraw exactly the levers the impersonated advisor lacks.
  'get_strategy_entitlements',
  // Downline Management's Invites panel — pure read, token never returned.
  'get_downline_invites',
  // Team page member panel — one person's Alts / FIA state, pure read.
  'get_member_strategy_flags',
  // Settings → Security — the caller's own login sessions, pure read.
  'get_my_sessions',
]);

// Allowlist of read-only edge functions. calculate-tax and solve-roth are pure
// computation, no DB writes — safe to call while impersonating so the wizard still works.
const READ_ONLY_FUNCTIONS = new Set<string>([
  'calculate-tax',
  'solve-roth',
]);

const MUTATING_METHODS = new Set(['insert', 'update', 'delete', 'upsert']);

// Synthetic success shape returned for blocked operations.
const safeOk = Object.freeze({
  data: null,
  error: null,
  count: null,
  status: 200,
  statusText: 'OK',
});

// A chainable thenable that swallows the rest of the postgrest builder chain
// and resolves to safeOk on await. Used in place of the real builder when
// a write is blocked.
function noopChain(): unknown {
  const handler: ProxyHandler<object> = {
    get(_target, prop) {
      if (prop === 'then') {
        return (onFulfilled: (v: unknown) => unknown) =>
          Promise.resolve(safeOk).then(onFulfilled);
      }
      if (prop === 'catch' || prop === 'finally') {
        // Standard Promise contract — pass through to a real promise.
        return (cb: () => unknown) => Promise.resolve(safeOk)[prop](cb as never);
      }
      // Any builder method (eq, neq, select, single, …) returns another noopChain.
      return () => noopChain();
    },
  };
  return new Proxy({}, handler);
}

function wrapFrom(realFrom: typeof realSupabase.from) {
  return ((table: Parameters<typeof realSupabase.from>[0]) => {
    const builder = realFrom(table);
    return new Proxy(builder as object, {
      get(target, prop, receiver) {
        if (impersonating && typeof prop === 'string' && MUTATING_METHODS.has(prop)) {
          return (..._args: unknown[]) => noopChain();
        }
        return Reflect.get(target, prop, receiver);
      },
    });
  }) as typeof realSupabase.from;
}

function wrapRpc(realRpc: typeof realSupabase.rpc) {
  return ((name: string, args?: unknown, options?: unknown) => {
    if (impersonating && !READ_ONLY_RPCS.has(name)) {
      return Promise.resolve(safeOk);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (realRpc as any)(name, args, options);
  }) as typeof realSupabase.rpc;
}

function wrapFunctions(realFunctions: typeof realSupabase.functions) {
  return new Proxy(realFunctions as object, {
    get(target, prop, receiver) {
      if (prop === 'invoke') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const realInvoke = (target as any).invoke.bind(target);
        return (name: string, options?: unknown) => {
          if (impersonating && !READ_ONLY_FUNCTIONS.has(name)) {
            return Promise.resolve(safeOk);
          }
          return realInvoke(name, options);
        };
      }
      return Reflect.get(target, prop, receiver);
    },
  }) as typeof realSupabase.functions;
}

export const supabase = new Proxy(realSupabase as object, {
  get(target, prop, receiver) {
    if (prop === 'from') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return wrapFrom((target as any).from.bind(target));
    }
    if (prop === 'rpc') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return wrapRpc((target as any).rpc.bind(target));
    }
    if (prop === 'functions') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return wrapFunctions((target as any).functions);
    }
    return Reflect.get(target, prop, receiver);
  },
}) as typeof realSupabase;
